import styles from './index.module.less'
import { useState, useEffect, ReactNode, useCallback, useLayoutEffect } from 'react'

import { Breadcrumb, Pagination, Menu, Layout, List } from '@arco-design/web-react'
import useLocale from '@/utils/useLocale'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { wrapper } from '@/store'
import type { InfluencersSitemapRes, Filter } from '@/types/api'
import { backendCodeProcessor, generateSsrCtx } from '@/api/request'
import getServerSidePropsCatch from '@/utils/getServerSidePropsCatch'
import { SUCCESS_CODE, BLUR_CODE, UNAUTHORIZED_CODE } from '@/utils/constants'
import useRegionOptions from '@/hooks/useRegionOptions'
import useCurrencyOptions from '@/hooks/useCurrencyOptions'
import { getInfluencersSitemapApi } from '@/api/influencer'

import { useSetState, useUpdateEffect } from 'ahooks'
import { useSelector } from 'react-redux'
import { StoreState } from '@/store'
import type { InfluencerListRes, InfluencerFiltersRes, InfluencersStarListRes } from '@/types/api'
import { Props } from '@/mobile-pages/sitemap/influencers'
import { RegionList, ReginItemProp, FooterLinks } from '@/pages/sitemap/products'

const MenuItem = Menu.Item
const Sider = Layout.Sider
const Header = Layout.Header
const Content = Layout.Content

export const DYNAMIC_INFLUENCERS_CATEGORY = `hot-{category}-influencers-in-{regionName}-on-tiktok`
const REGEX = /hot-(.+?)-influencers-in-(.+?)-on-tiktok/

export const getCategoryString = (currentCategoryNameSlug = '', currentRegionName = '') => {
  return DYNAMIC_INFLUENCERS_CATEGORY.replace('{category}', currentCategoryNameSlug || '').replace(
    '{regionName}',
    currentRegionName
  )
}

export const getUrlField = (
  str: string
): { urlCategoryNameSlug: string; urlRegion: string } | Record<string, unknown> => {
  const match = str.match(REGEX)

  if (match) {
    const urlCategoryNameSlug = match[1]
    const urlRegion = match[2]

    return {
      urlCategoryNameSlug: urlCategoryNameSlug,
      urlRegion: urlRegion
    }
  } else {
    return {}
  }
}

export const useFooterLinks = ({ category, region }: { category: string; region: string }) => {
  const t = useLocale()
  const curCategory = category.toLowerCase() === 'all' ? '' : category

  return [
    {
      name: t['seo.sitemap.influencers.tag1'].replace('{category}', curCategory || '').replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.influencers.tag2'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.influencers.tag3'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.influencers.tag4'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.influencers.tag5'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    },
    {
      name: t['seo.sitemap.influencers.tag6'].replace('{category}', curCategory).replace('{region}', region),
      href: '#'
    }
  ]
}

type InfluencerItemProp = InfluencersSitemapRes[number]

export const InfluencerItem = ({ item }: { item: InfluencerItemProp }) => {
  const t = useLocale()
  return (
    <div className={styles.listItem}>
      <Link href={`/influencers/${item.influencer_id}`}>
        <a>
          <div className={styles.mb6} style={{ fontWeight: 700, color: 'rgb(64, 61, 90)', wordBreak: 'break-all' }}>
            {item.influencer_name}
          </div>
          <div className={styles.mb6} style={{ wordBreak: 'break-all' }}>
            <span style={{ fontWeight: 400, color: '#898e99' }}>@{item.unique_id}</span>
            <span className={styles.ml10}>
              {t['common.region']}: {item.region.name}
            </span>
          </div>
          {item.bio && <div style={{ wordBreak: 'break-all' }}>BIO: {item.bio}</div>}
        </a>
      </Link>
    </div>
  )
}

export const useBreadcrumbs = ({
  currentCategoryObj,
  currentRegionObj
}: {
  currentCategoryObj?: Filter
  currentRegionObj?: ReginItemProp
}) => {
  const t = useLocale()
  return [
    {
      name: t['menu.platform.home'],
      href: '/'
    },
    {
      name: t['seo.sitemap.influencer_breadcrumb'],
      href: '/influencers'
    },
    {
      name: currentCategoryObj?.name,
      href: `/sitemap/influencers/${getCategoryString(currentCategoryObj?.name_slug, currentRegionObj?.nameSlug)}/1`
    },
    {
      name: currentRegionObj?.name,
      href: ''
    }
  ]
}

const InfluencerSitemap = ({ filtersFromSSR, dataFromSSR }: Props) => {
  const { influencer_categories } = filtersFromSSR || {}
  const t = useLocale()
  const [activeCategoryKey, setActiveCategoryKey] = useState(influencer_categories[0]?.key || 'All')

  const [dataList, setDataList] = useState<InfluencersSitemapRes>(dataFromSSR?.data || [])
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const { region } = useSelector((state: StoreState) => state.ssr)
  const { regionOptions } = useRegionOptions()
  const [pageObj, setPageObj] = useSetState({
    total: dataFromSSR.meta?.total || 0,
    pageSize: dataFromSSR.meta?.per_page || 200
  })
  const originRegionObj = regionOptions.find((r) => r.region === region)

  const currentUrlCategory = router.query.influencers_category
  const currentUrlPage = router.query.page || '1'
  const currentCategoryObj = influencer_categories.find((c) => c.key === activeCategoryKey)
  const currentCategoryNameSlug = currentCategoryObj?.name_slug
  const [currentRegionObj, setCurrentRegionObj] = useState<ReginItemProp | undefined>(originRegionObj)

  const breadcrumbs = useBreadcrumbs({ currentRegionObj, currentCategoryObj })
  const { urlCategoryNameSlug, urlRegion } = getUrlField(currentUrlCategory as string)

  useLayoutEffect(() => {
    if (urlCategoryNameSlug && urlRegion) {
      const curActiveCategoryKey = influencer_categories.find((c) => c.name_slug === urlCategoryNameSlug)?.key
      curActiveCategoryKey && setActiveCategoryKey(curActiveCategoryKey)
      const curReginObj = regionOptions.find((r) => r.nameSlug === urlRegion)
      curReginObj && setCurrentRegionObj(curReginObj)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const fetchList = useCallback(
    async (params: { influencer_categories: string; page: string; region: string }) => {
      setLoading(true)
      getInfluencersSitemapApi(params)
        .then((res) => {
          if (res.data.code === SUCCESS_CODE) {
            setDataList(res.data.data || [])
            setPageObj({
              total: res.data.meta?.total,
              pageSize: res.data.meta?.per_page
            })
          }
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [setPageObj]
  )

  useUpdateEffect(() => {
    fetchList({
      influencer_categories: influencer_categories.find((c) => c.key === activeCategoryKey)?.id || '',
      page: currentUrlPage as string,
      region: currentRegionObj?.region
    })
  }, [activeCategoryKey, currentRegionObj?.region, currentUrlPage, fetchList, influencer_categories])

  const handleReginClick = (item: ReginItemProp) => {
    setCurrentRegionObj(item)

    router.replace(
      {
        pathname: router.basePath,
        query: {
          influencers_category: getCategoryString(currentCategoryNameSlug, item.nameSlug),
          page: '1'
        }
      },
      undefined,
      { scroll: false, shallow: true }
    )
  }

  return (
    <div className={styles.influencersSitemap}>
      <Layout className="layout-collapse-demo">
        <Sider breakpoint="xl">
          <div className="logo" />
          <Menu
            selectedKeys={[activeCategoryKey]}
            onClickMenuItem={(key) => {
              setActiveCategoryKey(key)
            }}
            className={styles.menuWrap}
            style={{ width: '100%' }}
          >
            {influencer_categories.map((item) => {
              return (
                <MenuItem key={item.key}>
                  {/* <IconHome /> */}
                  <Link
                    href={{
                      pathname: router.pathname,
                      query: {
                        influencers_category: getCategoryString(item.name_slug, currentRegionObj?.nameSlug),
                        page: '1'
                      }
                    }}
                  >
                    <a>{item.name}</a>
                  </Link>
                </MenuItem>
              )
            })}
          </Menu>
        </Sider>
        <Layout>
          <Header style={{ paddingLeft: 20 }}>
            <h1>
              {t['seo.sitemap.influencer_h1']
                .replace('{category}', currentCategoryObj?.name || '')
                .replace('{regionName}', currentRegionObj?.name || region)}
            </h1>
          </Header>
          <Layout style={{ padding: '0 24px' }}>
            <Breadcrumb style={{ margin: '16px 0' }}>
              {breadcrumbs.map((item, index) => {
                return (
                  <Breadcrumb.Item href={item.href} key={index}>
                    {item.name}
                  </Breadcrumb.Item>
                )
              })}
            </Breadcrumb>
            <Content>
              <RegionList
                onClick={handleReginClick}
                currentCategoryNameSlug={currentCategoryNameSlug}
                currentRegionObj={currentRegionObj}
                categoryKey="influencers_category"
              />
              <List loading={loading} className={styles.listWrap} style={{ marginBottom: '20px' }}>
                {dataList.map((item, index) => {
                  return <InfluencerItem item={item} key={index} />
                })}
                {!loading && dataList.length > 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Pagination
                      className={styles.pageWrap}
                      total={pageObj.total}
                      pageSize={pageObj.pageSize}
                      current={currentUrlPage ? +currentUrlPage : 1}
                      itemRender={(page: number, type: 'page' | 'more' | 'prev' | 'next', originElement: ReactNode) => {
                        if (type === 'page') {
                          return (
                            <Link
                              href={{
                                pathname: router.pathname,
                                query: {
                                  influencers_category: getCategoryString(
                                    currentCategoryNameSlug,
                                    currentRegionObj?.nameSlug
                                  ),
                                  page: `${page}`
                                }
                              }}
                            >
                              <a>{page}</a>
                            </Link>
                          )
                        }
                        return originElement
                      }}
                    />
                  </div>
                ) : null}
              </List>
              <FooterLinks
                category={currentCategoryObj?.name || ''}
                region={currentRegionObj?.name || ''}
                useFooterLinks={useFooterLinks}
              />
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </div>
  )
}

export default InfluencerSitemap
